import React, { useMemo, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Dropdown, Menu } from 'antd';
import { DropDownProps } from 'antd/lib/dropdown';
import LoadingIcon from '@ant-design/icons/LoadingOutlined';
import CloseIcon from 'shared/assets/images/close-icon.svg';
import DropdownArrowIcon from 'shared/assets/images/dropdown-arrow-icon.svg';
import { formatDuration } from 'shared/utils/helpers';
import './style.less';

export interface SessionControlButtonProps {
  small?: boolean;
  full?: boolean;
  text?: string;
  className?: string;
  menuClassName?: string;
  disabled?: boolean;
  active?: boolean;
  loading?: boolean;
  pulse?: boolean;
  primary?: boolean;
  muted?: boolean;
  icon?: React.ReactNode;
  activeIcon?: React.ReactNode;
  disabledIcon?: React.ReactNode;
  timer?: number;
  menuItems?: SessionControlMenuItem[];
  additionalContent?: React.ReactNode;
  onClick?: (ev: any) => void;
  placement?: DropDownProps['placement'];
}

interface SessionControlMenuItem {
  key: string | number;
  label: React.ReactNode;
  icon: React.ReactNode;
  isDefault?: boolean;
  onClick: () => void;
}

export const SessionControlButton: React.FC<SessionControlButtonProps> = b => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (b.disabled) {
      setIsMenuOpen(false);
    }
  }, [b.disabled]);

  const defaultMenuItem = useMemo(() => {
    if (!b.menuItems) return false;
    return b.menuItems.find(({ isDefault }) => isDefault);
  }, [b.menuItems]);

  const menu = useMemo(() => {
    if (!b.menuItems) return null;
    // Menu is rendered outside of button DOM tree, so give it its own top-level
    // class name to make it clear it can't inherit styles.
    return (
      <Menu
        className={clsx(
          'SessionControlMenu',
          b.primary && 'SessionControlMenu--primary',
          b.active && 'SessionControlMenu--active',
          isMenuOpen && 'SessionControlMenu--open',
          b.menuClassName,
        )}
      >
        <div className="SessionControlMenu__content">
          {b.menuItems.map(mi => (
            <button
              key={mi.key}
              className="SessionControlMenu__content-item"
              onClick={e => {
                e.stopPropagation();
                setIsMenuOpen(false);
                mi.onClick();
              }}
            >
              <div className="SessionControlMenu__content-item-icon">{mi.icon}</div>
              <div className="SessionControlMenu__content-item-label">{mi.label}</div>
            </button>
          ))}
          {!!b.additionalContent && (
            <div className="SessionControlMenu__secondary-content">
              {b.additionalContent}
            </div>
          )}
        </div>
      </Menu>
    );
  }, [
    b.menuItems,
    b.primary,
    b.active,
    b.menuClassName,
    b.additionalContent,
    isMenuOpen,
  ]);

  const hasIcon = (b.disabled && b.disabledIcon) || b.loading || b.active || b.icon;
  const OuterWrapper = defaultMenuItem ? 'div' : 'button';
  const InnerWrapper = defaultMenuItem ? 'button' : 'div';

  const button = (
    <OuterWrapper
      className={clsx(
        'SessionControlButton',
        b.small && 'SessionControlButton--small',
        b.primary && 'SessionControlButton--primary',
        b.muted && 'SessionControlButton--muted',
        b.disabled && 'SessionControlButton--disabled',
        b.active && 'SessionControlButton--active',
        b.pulse && 'SessionControlButton--pulse',
        !b.text && 'SessionControlButton--notext',
        b.menuItems && 'SessionControlButton--hasMenu',
        defaultMenuItem && 'SessionControlButton--hasDefaultMenuItem',
        isMenuOpen && 'SessionControlButton--menuOpen',
        b.className,
      )}
      onClick={b.onClick}
      disabled={b.disabled}
    >
      <InnerWrapper
        onClick={
          defaultMenuItem
            ? e => {
                e.stopPropagation();
                defaultMenuItem.onClick();
              }
            : undefined
        }
        disabled={b.disabled}
      >
        {hasIcon && (
          <div className="SessionControlButton__icon">
            {b.disabled && b.disabledIcon ? (
              b.disabledIcon
            ) : b.loading ? (
              <LoadingIcon />
            ) : b.active ? (
              b.activeIcon || <CloseIcon className="close" />
            ) : (
              b.icon
            )}
          </div>
        )}
        {!!b.text && <div className="SessionControlButton__text">{b.text}</div>}
        {!!b.timer && (
          <div className="SessionControlButton__timer">{formatDuration(b.timer)}</div>
        )}
      </InnerWrapper>
      {menu && (
        <InnerWrapper
          className="SessionControlButton__arrow"
          onClick={
            defaultMenuItem
              ? e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }
              : undefined
          }
          disabled={b.disabled}
        >
          <DropdownArrowIcon />
        </InnerWrapper>
      )}
    </OuterWrapper>
  );

  if (menu) {
    return (
      <Dropdown
        visible={isMenuOpen}
        overlay={menu}
        trigger={defaultMenuItem && !isMenuOpen ? [] : ['click']}
        onVisibleChange={setIsMenuOpen}
        disabled={b.disabled}
        placement={b.placement || 'bottomLeft'}
      >
        {button}
      </Dropdown>
    );
  }

  return button;
};
